// Pediatric medication reference for ER use.
// Dose ranges follow common pediatric formularies (BNFc / Nelson / WHO guidance).
// Always cross-check with local protocols.
// `volume_per_mg` is used to compute the liquid volume (mL) for a single dose
// based on the standard formulation listed under `formulation`.

export const MEDICATIONS = [
  {
    id: 'adrenaline-im',
    name: 'Adrenaline (1:1000) — IM Anaphylaxis',
    route: 'IM',
    dose_mg_per_kg: 0.01,
    dose_range: [0.01, 0.01],
    frequency: 'repeat after 5–15 min if needed',
    max_single_dose_mg: 0.5,
    max_daily_mg_per_kg: null,
    max_daily_total_mg: null,
    notes: '0.01 mg/kg of 1:1000 (= 0.01 ml/kg). Max 0.5 mg single dose. Anterolateral thigh.',
    custom_volume_label: 'volume of 1:1000 (ml)',
    volume_per_mg: 1, // 1 mg/ml for 1:1000
  },
  {
    id: 'ambroxol',
    name: 'Ambroxol',
    route: 'PO',
    dose_mg_per_kg: 0.5,           // per-dose value (1.2–1.6 mg/kg/day ÷ 3)
    dose_range: [0.4, 0.53],
    frequency: 'every 8 hours (TDS)',
    max_single_dose_mg: 30,
    max_daily_mg_per_kg: 1.6,
    max_daily_total_mg: 90,
    notes: 'Mucolytic. Total daily 1.2–1.6 mg/kg/day divided in 3 doses.',
    formulation: 'Syrup 15 mg / 5 ml',
    custom_volume_label: 'Volume (Syrup 15 mg / 5 ml)',
    volume_per_mg: 5 / 15, // ml per mg → 0.333
  },
  {
    id: 'amoxicillin',
    name: 'Amoxicillin',
    route: 'PO',
    dose_mg_per_kg: 25,
    dose_range: [20, 40],
    frequency: 'every 8 hours (TDS)',
    max_single_dose_mg: 1000,
    max_daily_mg_per_kg: 90,
    max_daily_total_mg: 3000,
    notes: 'High-dose (80–90 mg/kg/day) for AOM, CAP. Reduce in renal failure.',
  },
  {
    id: 'azithromycin',
    name: 'Azithromycin',
    route: 'PO',
    dose_mg_per_kg: 10,
    dose_range: [5, 10],
    frequency: 'every 24 hours x 3–5 days',
    max_single_dose_mg: 500,
    max_daily_mg_per_kg: 10,
    max_daily_total_mg: 500,
    notes: 'Day 1: 10 mg/kg (max 500 mg). Days 2–5: 5 mg/kg (max 250 mg).',
  },
  {
    id: 'cefixime',
    name: 'Cefixime',
    route: 'PO',
    dose_mg_per_kg: 1.5,           // per-dose; range 1.5–3 mg/kg/dose
    dose_range: [1.5, 3],
    frequency: 'every 12 hours (BID)',
    max_single_dose_mg: 200,
    max_daily_mg_per_kg: 6,
    max_daily_total_mg: 400,
    notes: '3–6 mg/kg/day in 2 divided doses. Reduce in renal impairment.',
    formulation: 'Dry Syrup 100 mg / 5 ml',
    custom_volume_label: 'Volume (Syrup 100 mg / 5 ml)',
    volume_per_mg: 5 / 100, // 0.05 ml per mg
  },
  {
    id: 'ceftriaxone',
    name: 'Ceftriaxone',
    route: 'IV / IM',
    dose_mg_per_kg: 50,
    dose_range: [50, 100],
    frequency: 'every 24 hours (OD)',
    max_single_dose_mg: 2000,
    max_daily_mg_per_kg: 100,
    max_daily_total_mg: 4000,
    notes: 'Use 100 mg/kg/day for meningitis (q12h). Avoid in neonates with calcium-containing IVs.',
  },
  {
    id: 'dexamethasone',
    name: 'Dexamethasone',
    route: 'PO / IV / IM',
    dose_mg_per_kg: 0.1,
    dose_range: [0.1, 0.6],
    frequency: 'single dose or every 6 hours',
    max_single_dose_mg: 16,
    max_daily_mg_per_kg: 0.6,
    max_daily_total_mg: 16,
    notes: 'Croup: 0.15–0.6 mg/kg single dose. Meningitis: 0.15 mg/kg q6h.',
  },
  {
    id: 'domperidone',
    name: 'Domperidone',
    route: 'PO',
    dose_mg_per_kg: 0.3,           // per-dose; range 0.2–0.4
    dose_range: [0.2, 0.4],
    frequency: 'every 8 hours (TDS)',
    max_single_dose_mg: 10,
    max_daily_mg_per_kg: 1.2,
    max_daily_total_mg: 30,
    notes: 'Anti-emetic. Use shortest duration possible; QT prolongation risk.',
    formulation: 'Syrup 5 mg / 5 ml (default) · also Drop 5 mg / ml',
    custom_volume_label: 'Volume (Syrup 5 mg / 5 ml)',
    volume_per_mg: 5 / 5, // 1 ml per mg
  },
  {
    id: 'ibuprofen',
    name: 'Ibuprofen',
    route: 'PO',
    dose_mg_per_kg: 10,
    dose_range: [5, 10],
    frequency: 'every 6–8 hours',
    max_single_dose_mg: 400,
    max_daily_mg_per_kg: 40,
    max_daily_total_mg: 1200,
    notes: 'Avoid in dehydration, renal impairment, or active GI bleeding.',
  },
  {
    id: 'metronidazole',
    name: 'Metronidazole',
    route: 'PO / IV',
    dose_mg_per_kg: 7.5,
    dose_range: [7.5, 10],
    frequency: 'every 8 hours',
    max_single_dose_mg: 500,
    max_daily_mg_per_kg: 30,
    max_daily_total_mg: 1500,
    notes: 'Anaerobic coverage. Avoid alcohol.',
  },
  {
    id: 'ondansetron',
    name: 'Ondansetron',
    route: 'PO / IV',
    dose_mg_per_kg: 0.1,
    dose_range: [0.1, 0.15],
    frequency: 'every 8 hours',
    max_single_dose_mg: 8,
    max_daily_mg_per_kg: 0.45,
    max_daily_total_mg: 24,
    notes: 'Acute gastroenteritis with vomiting. Check QTc if repeated dosing.',
  },
  {
    id: 'paracetamol-oral',
    name: 'Paracetamol (Acetaminophen)',
    route: 'PO / PR',
    dose_mg_per_kg: 10,
    dose_range: [10, 15],
    frequency: 'every 4–6 hours',
    max_single_dose_mg: 1000,
    max_daily_mg_per_kg: 75,
    max_daily_total_mg: 4000,
    notes: 'Antipyretic / analgesic. Reduce in hepatic impairment.',
  },
];

export const getMedicationById = (id) => MEDICATIONS.find((m) => m.id === id);
